// src/pages/task/TaskTemplateDetailPage.tsx

import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useTaskTemplate, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return String(dateStr); }
}

/**
 * Task template detail page.
 */
export const TaskTemplateDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTaskTemplate();
  const taskStore = useTask();
  const { t } = useI18n();
  const item = store.currentItem;

  useEffect(() => {
    if (id) {
      store.GetTemplate(id);
    }
    return () => {
      store.clearCurrent();
    };
  }, [id]);

  const handleDelete = async () => {
    if (!id || !confirm(t('task.deleteConfirm'))) return;
    try {
      await store.DeleteTemplate(id);
      navigate('/task-templates');
    } catch {
      // Error is handled by store
    }
  };

  const handleCreateFromTemplate = async () => {
    if (!id) return;
    try {
      const task: any = await taskStore.CreateTaskFromTemplate(id, {} as any);
      const newId = task?.entity?.id || task?.task?.entity?.id;
      navigate(newId ? `/tasks/${newId}` : '/tasks');
    } catch {
      // Error is handled by store
    }
  };

  if (store.isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-3 w-48 bg-muted rounded mb-4" />
          <div className="h-8 w-64 bg-muted rounded" />
        </div>
        <Card>
          <CardHeader>
            <div className="h-5 w-40 bg-muted rounded animate-pulse" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="grid grid-cols-3 gap-4 animate-pulse">
                  <div className="h-4 w-24 bg-muted rounded" />
                  <div className="h-4 w-40 bg-muted rounded col-span-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/task-templates" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/task-templates" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/tasks" className="text-muted-foreground hover:text-foreground">
              {t('task.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link to="/task-templates" className="text-muted-foreground hover:text-foreground">
              {t('task.templates')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{String((item as any)?.name ?? t('task.details'))}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{String((item as any)?.name ?? '')}</h1>
        </div>
        <div className="flex gap-2">
          <Link to={`/task-templates/${id}/edit`}>
            <Button variant="secondary">{t('common.edit')}</Button>
          </Link>
          <Button
            variant="secondary"
            isLoading={store.isSaving}
            onClick={handleCreateFromTemplate}
          >
            {t('task.createFromTemplate')}
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            {t('common.delete')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('task.templateDetails')}</CardTitle>
          <CardDescription>{t('task.templateInfo')}</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Name</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.name ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Description</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.description ?? '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Title</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.default_title ?? '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Description</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item as any)?.default_description ?? '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Type</dt>
              <dd className="text-sm text-foreground col-span-2">
                {(item as any)?.default_type
                  ? formatLabel(String((item as any).default_type), 'TASK_TYPE_')
                  : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Priority</dt>
              <dd className="text-sm text-foreground col-span-2">
                {(item as any)?.default_priority
                  ? formatLabel(String((item as any).default_priority), 'TASK_PRIORITY_')
                  : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Default Tags</dt>
              <dd className="text-sm text-foreground col-span-2">
                {(item as any)?.default_tags?.length > 0
                  ? (item as any).default_tags.map((tag: string) => (
                      <Badge key={tag} variant="secondary" className="mr-1 mb-1">
                        {tag}
                      </Badge>
                    ))
                  : '--'}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Active</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge variant={(item as any)?.is_active ? 'success' : 'secondary'}>
                  {(item as any)?.is_active ? t('common.active') : t('common.inactive')}
                </Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {formatDate((item as any)?.created_at)}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Updated At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {formatDate((item as any)?.updated_at)}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Default Checklists */}
      {(item as any)?.default_checklists?.checklists?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('task.defaultChecklists')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {(item as any).default_checklists.checklists.map((checklist: any, idx: number) => (
                <li key={checklist.id || idx} className="text-sm text-foreground">
                  <span className="font-medium">{String(checklist.title ?? checklist.name ?? `Checklist ${idx + 1}`)}</span>
                  {checklist.items?.length > 0 && (
                    <ul className="ml-4 mt-1 space-y-1">
                      {checklist.items.map((ci: any, ciIdx: number) => (
                        <li key={ci.id || ciIdx} className="text-muted-foreground">
                          {String(ci.title ?? ci.text ?? '')}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Default Labels */}
      {(item as any)?.default_labels?.labels?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('task.defaultLabels')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(item as any).default_labels.labels.map((label: any) => (
                <Badge
                  key={label.id || label.name}
                  variant="secondary"
                  style={label.color ? { backgroundColor: label.color, color: '#fff' } : undefined}
                >
                  {String(label.name ?? '')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default TaskTemplateDetailPage;
